'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import MarketingNav from '@/app/marketing/components/MarketingNav';
import MarketingFooter from '@/app/marketing/components/MarketingFooter';

const MODULES = [
  {
    id: 'inventory',
    icon: '💎',
    title: 'Inventory Intelligence',
    tagline: "Know exactly where every diamond is — and what it's worth right now.",
    color: 'text-violet-400',
    border: 'border-violet-500/30',
    bg: 'bg-violet-500/8',
    features: [
      {
        name: 'Full 4C attribute tracking',
        desc: 'Cut, carat, color, clarity, fluorescence, and more — per stone.',
      },
      {
        name: 'QR code generation & scanning',
        desc: 'Print QR labels, scan with any phone, instantly pull up stone details.',
      },
      {
        name: 'GIA certificate verification',
        desc: 'Link and verify GIA reports directly from the inventory record.',
      },
      { name: 'Photo & video gallery', desc: 'Attach multiple images and 360° videos per stone.' },
      { name: 'Bulk CSV import', desc: 'Import your existing spreadsheet inventory in minutes.' },
      {
        name: 'Aging & hold alerts',
        desc: 'Automatic notifications when stones have been idle too long.',
      },
      {
        name: 'Location tracking',
        desc: 'Know if a stone is in the safe, on display, on memo, or with a client.',
      },
      {
        name: 'Inventory aging reports',
        desc: 'See which stones are tying up capital and need attention.',
      },
    ],
  },
  {
    id: 'pricing',
    icon: '📈',
    title: 'Precision Pricing',
    tagline: 'Stop guessing. Price every stone correctly, every time.',
    color: 'text-amber-400',
    border: 'border-amber-500/30',
    bg: 'bg-amber-500/8',
    features: [
      {
        name: 'Live Rapaport price feed',
        desc: 'Real-time Rap prices integrated directly into your inventory.',
      },
      {
        name: 'Custom markup rule engine',
        desc: 'Set markup by shape, quality tier, carat range, or client type.',
      },
      {
        name: 'Per-client pricing tiers',
        desc: 'Different prices for retail, wholesale, and VIP clients — automatically.',
      },
      {
        name: 'Profit margin calculator',
        desc: 'See your margin on every stone before you quote.',
      },
      {
        name: 'Currency toggle (THB / USD)',
        desc: 'Switch between Thai Baht and US Dollar with live exchange rates.',
      },
      {
        name: 'Discount approval workflow',
        desc: 'Require manager approval for discounts above a threshold.',
      },
      { name: 'Price history log', desc: 'Track how pricing has changed over time for any stone.' },
      {
        name: 'Quotation builder',
        desc: 'Generate professional PDF quotes with your branding in seconds.',
      },
    ],
  },
  {
    id: 'crm',
    icon: '🤝',
    title: 'Relationship CRM',
    tagline: 'Every client relationship, every deal, every follow-up — in one place.',
    color: 'text-indigo-400',
    border: 'border-indigo-500/30',
    bg: 'bg-indigo-500/8',
    features: [
      {
        name: 'Client & supplier profiles',
        desc: 'Complete contact records with purchase history, preferences, and notes.',
      },
      {
        name: 'Wishlist management',
        desc: 'Track exactly what each client is looking for and match stones automatically.',
      },
      {
        name: 'Follow-up task queue',
        desc: 'Never miss a follow-up with automated reminders and task assignments.',
      },
      {
        name: 'Deal pipeline stages',
        desc: 'Visual Kanban pipeline from first contact to closed deal.',
      },
      {
        name: 'Email automation',
        desc: 'Send personalized emails when new matching stones arrive.',
      },
      { name: 'Activity timeline', desc: 'Full history of every interaction with each contact.' },
      {
        name: 'Memo & consignment tracking',
        desc: 'Track stones out on memo with automatic aging and return alerts.',
      },
      {
        name: 'Supplier payables',
        desc: 'Manage what you owe suppliers and when payments are due.',
      },
    ],
  },
  {
    id: 'appraisal',
    icon: '🔬',
    title: 'Appraisal Tools',
    tagline: 'Professional appraisal reports, GIA-linked and compliance-ready.',
    color: 'text-emerald-400',
    border: 'border-emerald-500/30',
    bg: 'bg-emerald-500/8',
    features: [
      {
        name: 'Appraisal report builder',
        desc: "Create professional appraisal reports with your firm's branding.",
      },
      {
        name: 'GIA verification API',
        desc: 'Verify certificate authenticity directly from the appraisal form.',
      },
      {
        name: 'Certificate archive',
        desc: 'Store and search all certificates with full-text search.',
      },
      {
        name: 'Compliance audit log',
        desc: 'Immutable log of all appraisal actions for regulatory compliance.',
      },
      { name: 'Digital signature', desc: 'Sign reports digitally and send to clients securely.' },
      { name: 'PDF export', desc: 'Export any appraisal as a branded, professional PDF.' },
      { name: 'Insurance valuation', desc: 'Generate insurance replacement value reports.' },
      {
        name: 'Historical value tracking',
        desc: "Track how a stone's appraised value has changed over time.",
      },
    ],
  },
];

export default function FeaturesPage() {
  const [activeModule, setActiveModule] = useState('inventory');
  const active = MODULES?.find((m) => m?.id === activeModule) ?? MODULES?.[0];

  return (
    <div
      className="min-h-screen bg-[#0a0e1a] text-[#e5e7eb]"
      style={{ fontFamily: 'var(--font-plus-jakarta-sans), system-ui, sans-serif' }}
    >
      <MarketingNav />
      <main id="main-content">
        {/* Hero */}
        <section className="pt-28 pb-16 px-4 sm:px-6 text-center relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full bg-violet-600/6 blur-[100px]" />
          </div>
          <div className="relative z-10 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-violet-500/30 bg-violet-500/8 text-violet-300 text-xs font-medium mb-6">
              Platform Features
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4 tracking-tight">
              Every Tool a Diamond Merchant Needs
            </h1>
            <p className="text-[#9ca3af] text-lg leading-relaxed">
              Four deeply integrated modules — Inventory, Pricing, CRM, and Appraisal — designed to
              work together seamlessly.
            </p>
          </div>
        </section>
        {/* Module tabs */}
        <section className="pb-20 px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            {/* Tab nav */}
            <div className="flex flex-wrap gap-2 justify-center mb-10">
              {MODULES?.map((m) => (
                <button
                  key={m?.id}
                  onClick={() => setActiveModule(m?.id)}
                  className={`flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium transition-all ${
                    activeModule === m?.id
                      ? `bg-white/10 text-white border border-white/20`
                      : 'text-[#9ca3af] hover:text-white hover:bg-white/5 border border-transparent'
                  }`}
                >
                  <span>{m?.icon}</span>
                  {m?.title?.split(' ')?.[0]}
                </button>
              ))}
            </div>

            {/* Active module detail */}
            <div
              id={active?.id}
              className={`rounded-2xl border ${active?.border} bg-gradient-to-b ${active?.bg} to-[#111827] overflow-hidden`}
            >
              <div className="p-8 border-b border-white/5">
                <div className="flex items-start gap-5">
                  <div
                    className={`w-14 h-14 rounded-2xl border ${active?.border} bg-[#0d1120] flex items-center justify-center text-3xl flex-shrink-0`}
                  >
                    {active?.icon}
                  </div>
                  <div>
                    <h2 className={`text-2xl font-bold text-white mb-1`}>{active?.title}</h2>
                    <p className="text-[#9ca3af]">{active?.tagline}</p>
                  </div>
                </div>
              </div>

              <div className="p-8">
                <div className="grid sm:grid-cols-2 gap-5">
                  {active?.features?.map((feat) => (
                    <div key={feat?.name} className="flex items-start gap-3">
                      <div
                        className={`w-5 h-5 rounded-full border flex items-center justify-center flex-shrink-0 mt-0.5 ${active?.border} bg-[#0d1120]`}
                      >
                        <svg
                          className={`w-3 h-3 ${active?.color}`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2.5}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-white">{feat?.name}</p>
                        <p className="text-xs text-[#9ca3af] mt-0.5 leading-relaxed">
                          {feat?.desc}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* All modules overview */}
        <section className="py-16 px-4 sm:px-6 bg-[#0d1120] border-y border-white/5">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-white text-center mb-10">
              All Four Modules at a Glance
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {MODULES?.map((m) => (
                <button
                  key={m?.id}
                  onClick={() => {
                    setActiveModule(m?.id);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className={`text-left rounded-2xl border ${m?.border} ${m?.bg} p-6 hover:-translate-y-1 transition-all duration-300 group`}
                >
                  <span className="text-3xl block mb-3">{m?.icon}</span>
                  <h3 className={`text-sm font-bold ${m?.color} mb-2`}>{m?.title}</h3>
                  <p className="text-xs text-[#9ca3af] leading-relaxed">{m?.tagline}</p>
                  <p className="text-xs text-[#8b93a3] mt-3 group-hover:text-white transition-colors">
                    {m?.features?.length} features →
                  </p>
                </button>
              ))}
            </div>
          </div>
        </section>
        {/* CTA */}
        <section className="py-16 px-4 sm:px-6 text-center">
          <div className="max-w-xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-4">Ready to See It in Action?</h2>
            <p className="text-[#9ca3af] mb-6">
              Book a personalized demo and we'll walk you through every module with your use case.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                href="/demo"
                className="px-8 py-4 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-[#0a0e1a] font-bold text-sm shadow-xl shadow-amber-500/20 transition-all hover:-translate-y-0.5"
              >
                Book a Demo
              </Link>
              <Link
                href="/pricing"
                className="px-8 py-4 rounded-xl border border-white/10 bg-white/5 hover:bg-white/8 text-white font-medium text-sm transition-all hover:-translate-y-0.5"
              >
                View Pricing
              </Link>
            </div>
          </div>
        </section>
      </main>
      <MarketingFooter />
    </div>
  );
}
